import ArchiveWorkOrderIcon from "@shared/svg/archive-work-order.svg?react";
import CalendarIcon from "@shared/svg/calendar-filled.svg?react";
import DisabledUnlink from "@shared/svg/wo-unlink-disabled.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import {
  ExternalIcon,
  EXTERNAL_ICON_SIZES,
  EXTERNAL_ICON_THUMB_BADGE_CLASS,
  NotificationCounter,
  COUNTER_SIZES,
} from "@shared/ui/Indicators";
import Tag from "@shared/ui/Tag";
import moment from "moment-timezone";
import { DotsThree, Trash, WarningCircle } from "phosphor-react";
import React, { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import usePopper from "react-overlays/usePopper";
import ReactTooltip from "react-tooltip";
import { Waypoint } from "react-waypoint";
import useOnClickOutside from "use-onclickoutside";

import { COLOR, ITEMS_BEFORE_PAGE, MAX_TEXT_INPUT_LENGTH } from "../constants";

import ticketTypes from "$/settings/enums/ticket/_types.json";
import { useChatStore } from "~/stores/chatStore";
import { ActionPopup } from "~/components/_popupMenu";
import { useAuth } from "~/components/general";
import { WorkOrderListSkeleton } from "~/components/skeletons/workOrders";
import { canUnarchiveWorkOrder } from "~/utils/workOrderArchive";
import WorkOrderDeleteAlert from "~/components/tickets/_workOrderDeleteAlert";
import { DueDateTag } from "~/components/tickets/DueDateTag.tsx";
import TicketItemAssigneesList from "~/components/tickets/ticketItemAssigneesList";
import WorkOrderListCard from "#/src/components/tickets/WorkOrderListCard";
import WorkOrderRow from "~/components/tickets/WorkOrderRow";
import WorkOrderTypeIcons from "~/components/tickets/WorkOrderTypeIcons";
import { useRoleManager } from "~/hooks/_useRoleManager";
import {
  ExclamationIcon,
  ProcedureCheckCircle,
  ProcedureCrossCircle,
  UnlinkIcon,
} from "~/icons/icons";
import { generateStatusBackgroundColor } from "~/utils/_colors";
import { trimText } from "~/utils/_initials";

const PreventiveMaintenanceDeleteIconStyles = {
  display: "inline-flex",
  alignItems: "center",
};

export const TicketItemOpen = ({
  data = [],
  path = "work/work-orders",
  ticketsLoading = false,
  onScrollToBottom = () => {},
  totalCount,
  loading,
  onArchive,
  onUnarchive,
  fromArchivedList = false,
  listQueryKey,
  listItemsLoaded,
  isSelectMode = false,
  isSelectedFn = () => false,
  onToggleItem = () => {},
  anySelected = false,
  showHierarchy = false,
  isArchived = false,
}) => {
  const { user } = useAuth();

  const [showDeleteAlert, setShowDeleteAlert] = React.useState(false);
  const deleteWorkOrderId = useRef(null);
  const hasDeleteWorkOrderSubWorkOrders = useRef(false);
  const { isOemAdmin, isOemTechnician } = useRoleManager(user.role);

  const onCloseAlert = () => {
    setShowDeleteAlert(false);
  };

  const allStatuses = user?.oem?.statuses || [];

  const handleDelete = (item) => {
    deleteWorkOrderId.current = item._id;
    hasDeleteWorkOrderSubWorkOrders.current = item.subWorkOrders?.length > 0;
    setShowDeleteAlert(true);
  };

  return (
    <>
      <div className="flex flex-col gap-sm">
        {data?.map((item, index) => {
          const isSelfCreatedTicket = item.createdBy?._id === user._id;
          const showArchive = onArchive && !item?.archived && isOemAdmin;
          const showUnarchive =
            onUnarchive && canUnarchiveWorkOrder(item, isOemAdmin);
          const showDelete =
            isOemAdmin || (isSelfCreatedTicket && !isOemTechnician);

          return (
            <React.Fragment key={item._id}>
              <div className="group flex items-center gap-sm">
                <div className="flex-1 min-w-0">
                  <WorkOrderRow
                    ticket={item}
                    allStatuses={allStatuses}
                    showArchive={showArchive}
                    showUnarchive={showUnarchive}
                    showDelete={showDelete}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onDelete={handleDelete}
                    path={path}
                    showHierarchy={showHierarchy}
                    fromArchivedList={fromArchivedList}
                    isArchived={isArchived}
                    listQueryKey={listQueryKey}
                    listItemsLoaded={listItemsLoaded}
                    isSelectMode={isSelectMode}
                    isSelectedFn={isSelectedFn}
                    anySelected={anySelected}
                    onToggleItem={onToggleItem}
                    isArchived={isArchived}
                  />
                </div>
              </div>
              {!ticketsLoading &&
                totalCount > data.length &&
                index === data.length - ITEMS_BEFORE_PAGE && (
                  <Waypoint onEnter={onScrollToBottom} />
                )}
            </React.Fragment>
          );
        })}
        {loading && data.length > 0 && <WorkOrderListSkeleton rowCount={2} />}
      </div>
      <WorkOrderDeleteAlert
        showAlert={showDeleteAlert}
        onClose={onCloseAlert}
        workOrderId={deleteWorkOrderId.current}
        hasSubWorkOrders={hasDeleteWorkOrderSubWorkOrders.current}
      />
    </>
  );
};

export const TicketListComponent = React.memo(
  ({
    status,
    allStatuses,
    title,
    facility,
    machine,
    machineSerialNumber,
    ticketID,
    ticketType,
    ticketChannel,
    ticketInternalChannel,
    maintenanceOn,
    hasOriginalEventBeenDeleted,
    setScrollPos = () => {},
    assignees = [],
    showDelete = false,
    onDelete = () => {},
    showArchive = false,
    onArchive,
    showUnarchive = false,
    onUnarchive,
    showActionMenu = false,
    showActionMenuOpen = false,
    onActionMenuToggle = () => {},
    onActionMenuClose = () => {},
    showUnlinkButton = false,
    onUnlink = () => {},
    compactTile = false,
    shortTitle = false,
    disabledUnlinkButton = false,
    hideUnlinkButton = false,
    scheduleDate = "",
    dueDate = "",
    isDisabled = false,
  }) => {
    const { messages } = useIntl();
    const { user } = useAuth();

    const unreadChannelMessages = useChatStore((s) => s.unreadChannelMessages);
    const notificationCounter =
      (unreadChannelMessages?.channels[ticketChannel] || 0) +
      (unreadChannelMessages?.channels[ticketInternalChannel] || 0);

    const [isHovered, setIsHovered] = useState(false);
    const styles = useMemo(() => {
      return {
        backgroundColor: isHovered
          ? ticketType.color
          : generateStatusBackgroundColor(ticketType?.color),
      };
    }, [isHovered, ticketType.color]);

    const statusItem = allStatuses?.find((item) => status === item._id);

    const handleMouseEnter = () => {
      setIsHovered(() => true);
      ReactTooltip.rebuild();
    };
    const handleMouseLeave = () => {
      setIsHovered(() => false);
    };

    const isClosedTicket =
      status === allStatuses?.[allStatuses?.length - 1]?._id;

    const dateFormat = user.dateFormat;

    const scheduledDateText = scheduleDate?.startTime
      ? moment(scheduleDate.startTime).format(dateFormat)
      : "";

    const ticketSecondaryDataJSX = !compactTile && (
      <>
        <div className="!w-36 flex justify-end ml-lg">
          <div>
            <TicketItemAssigneesList assignees={assignees} />
          </div>
        </div>

        <DueDateTag
          dueDate={dueDate}
          isClosedTicket={isClosedTicket}
          className="ml-sm"
        />

        {scheduledDateText && !isClosedTicket && (
          <div
            className="ml-lg"
            data-tip={messages?.common?.scheduleDate + ": " + scheduledDateText}
            data-for="global-tooltip"
            data-event="mouseenter"
            data-event-off="mouseleave"
          >
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-tertiary"
              className="no-styles font-semibold bg-warning-lightest text-warning whitespace-nowrap py-2xs px-xs rounded-md flex items-center group-hover:bg-warning group-hover:text-white"
            >
              <span className="mr-2xs leading-none">
                <CalendarIcon className="stroke-current" />
              </span>
              {scheduledDateText}
            </BodyText>
          </div>
        )}
      </>
    );

    const mainDataJSX = (
      <div
        className={`ticket-item-wrapper ${
          isClosedTicket ? " closed-ticket-wrapper" : ""
        } `}
        onClick={() => {
          setScrollPos({
            x: window.scrollX,
            y: window.scrollY,
            index: 0,
            isVirtualList: false,
          });
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`left-side-content ${
            compactTile ? "" : "w-4/5 lg:w-[45%]"
          } ${shortTitle ? "w-4/5" : ""}`}
        >
          <div
            className="work-order-icon-container"
            style={styles}
            data-tip={
              ticketType?.isInternal
                ? ""
                : messages?.workOrderTypes?.externalWorkOrderTooltip
            }
            data-for="global-tooltip"
            data-event="mouseenter"
            data-event-off="mouseleave"
          >
            <WorkOrderTypeIcons
              size={20}
              icon={ticketType.icon}
              strokeColor={isHovered ? COLOR.$white_v2 : ticketType.color}
            />

            {ticketType.isInternal ? null : (
              <ExternalIcon
                size={EXTERNAL_ICON_SIZES.ACCORDION}
                className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
              />
            )}

            {parseInt(notificationCounter) > 0 && (
              <NotificationCounter
                size={COUNTER_SIZES.LARGE}
                className="absolute -bottom-3xs -left-3xs border border-solid border-primary"
                count={notificationCounter}
                isSizedCounter
              />
            )}
          </div>
          <div className="title-wrapper u-flex-1 u-padding-r-5 u-overflow-hidden">
            <Headline
              size={HEADLINE_SIZES.X_SMALL}
              className="overflow-hidden text-ellipsis whitespace-nowrap"
            >
              <TicketTitleText
                title={title}
                ticketType={ticketType}
                maintenanceOn={maintenanceOn}
                hasOriginalEventBeenDeleted={hasOriginalEventBeenDeleted}
              />
            </Headline>

            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-secondary"
              className="flex items-center no-styles mt-sm lg:mt-0"
            >
              #{ticketID}
              {facility ? (
                <span className="inline-flex items-center">
                  <span>&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                  {facility}
                </span>
              ) : (
                <span className="inline-flex items-center">
                  <span>&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                  <ExclamationIcon />
                  &nbsp;&nbsp;
                  {messages?.tickets?.["deletedConnection"]}
                </span>
              )}
              {machine ? (
                <span className="inline-flex items-center">
                  <span>&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                  {machine}
                </span>
              ) : (
                <span className="inline-flex items-center">
                  <span>&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                  <ExclamationIcon />
                  &nbsp;&nbsp;
                  {messages["hierarchy"]["assets"]["deletedAsset"]}
                </span>
              )}
              {machineSerialNumber && (
                <span className="inline-flex items-center">
                  <span>&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                  {machineSerialNumber}
                </span>
              )}
            </BodyText>
            <div className="flex items-center lg:hidden mt-sm">
              {ticketSecondaryDataJSX}
            </div>
          </div>
        </div>
        <div className="flex max-w-[50%] ">
          <div className="hidden lg:flex items-center w-full">
            {ticketSecondaryDataJSX}
          </div>
          <Tag
            className="ml-lg"
            label={statusItem?.label}
            style={{
              backgroundColor: isHovered
                ? statusItem?.color
                : generateStatusBackgroundColor(statusItem?.color),
              color: isHovered ? COLOR.$white_v2 : statusItem?.color,
            }}
            isHoverable={false}
          />

          {showActionMenu && (
            <div
              className={`ml-lg relative ticket-list-item-actions ${
                showActionMenuOpen ? "ticket-list-item-actions--open" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant={BUTTON_VARIANTS.LINK}
                leadingIcon={<DotsThree className="text-secondary" size={16} />}
                tooltipId="ticket-actions"
                tooltipText={messages?.common?.more}
                onClick={onActionMenuToggle}
              />
              {showActionMenuOpen && (
                <ActionPopup
                  actions={[
                    ...(showArchive
                      ? [
                          {
                            label: messages?.tickets?.archiveWorkOrder,
                            onClick: onArchive,
                            className: "",
                            icon: (
                              <ArchiveWorkOrderIcon
                                width={16}
                                height={16}
                                className="flex-shrink-0"
                              />
                            ),
                          },
                        ]
                      : []),
                    ...(showUnarchive
                      ? [
                          {
                            label: messages?.tickets?.unarchiveWorkOrder,
                            onClick: onUnarchive,
                            className: "",
                            icon: (
                              <ArchiveWorkOrderIcon
                                width={16}
                                height={16}
                                className="flex-shrink-0"
                              />
                            ),
                          },
                        ]
                      : []),
                    ...(showDelete
                      ? [
                          {
                            label: messages?.tickets?.deleteWorkOrder,
                            onClick: onDelete,
                            className: "delete",
                            icon: (
                              <Trash
                                size={16}
                                className="flex-shrink-0 popup-menu-v2-item-icon--delete"
                              />
                            ),
                          },
                        ]
                      : []),
                  ]}
                  close={onActionMenuClose}
                  position="below-parent"
                />
              )}
            </div>
          )}

          {!showActionMenu && showDelete && (
            <Button
              variant={BUTTON_VARIANTS.LINK}
              className="ml-lg"
              leadingIcon={<Trash className="text-secondary" size={16} />}
              tooltipId="delete-ticket"
              tooltipText={messages?.common?.delete}
              onClick={onDelete}
            />
          )}

          {showUnlinkButton && (
            <div className="flex p-0">
              {hideUnlinkButton ? (
                <div className="w-3xl h-lg"></div>
              ) : (
                <Button
                  variant={BUTTON_VARIANTS.LINK}
                  className={`flex ml-4 items-end ${
                    disabledUnlinkButton ? "pointer-events-none" : ""
                  }`}
                  leadingIcon={
                    disabledUnlinkButton ? (
                      <DisabledUnlink className="w-lg h-lg" />
                    ) : (
                      <UnlinkIcon size={16} />
                    )
                  }
                  tooltipText={messages?.tickets?.unlinkWorkOrder}
                  tooltipPosition="bottom"
                  tooltipId="unlink-ticket"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnlink();
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );

    return isDisabled ? (
      <div
        data-tip={
          ticketType?.isInternal ? "" : messages?.workOrderTypes?.noAccess
        }
        data-for="global-tooltip"
        data-event="mouseenter"
        data-event-off="mouseleave"
      >
        {mainDataJSX}
      </div>
    ) : (
      mainDataJSX
    );
  },
);

export const ListItemBoxed = ({ children, className }) => {
  return <div className={`list-item--boxed ${className}`}>{children}</div>;
};

export const TicketTitleText = ({
  title,
  ticketType,
  hasOriginalEventBeenDeleted,
  maintenanceOn,
}) => {
  const { messages } = useIntl();
  const isPMTicket =
    ticketTypes[ticketType] === ticketTypes.PreventiveMaintenance;

  const { user } = useAuth();
  const dateFormat = user.dateFormat;

  return (
    <>
      <div className="text-ellipsis whitespace-nowrap overflow-hidden">
        {trimText(title, 75)}
      </div>

      {isPMTicket && hasOriginalEventBeenDeleted && (
        <span style={PreventiveMaintenanceDeleteIconStyles}>
          &nbsp; (<ExclamationIcon /> &nbsp;&nbsp;
          {messages?.tickets?.preventiveEventDeletedMessage})
        </span>
      )}

      {isPMTicket &&
        !hasOriginalEventBeenDeleted &&
        maintenanceOn &&
        !maintenanceOn?.includes("1970-01-01") && (
          <>
            ({messages?.tickets?.preventiveTicketTitlePart}{" "}
            {moment(maintenanceOn).format(dateFormat) || ""})
          </>
        )}
    </>
  );
};

export const EditTicketTitleText = ({
  title,
  ticketType,
  hasOriginalEventBeenDeleted,
  maintenanceOn,
  handleUpdateTitle,
  disabled = false,
}) => {
  const [newTitle, setNewTitle] = useState(title);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  const container = useRef();
  const toolTip = useRef();
  const inputRef = useRef(null);

  const { messages } = useIntl();

  const { user } = useAuth();
  const dateFormat = user.dateFormat;

  const { styles, attributes } = usePopper(container.current, toolTip.current, {
    placement: "bottom-end",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const isPMTicket =
    ticketTypes[ticketType] === ticketTypes.PreventiveMaintenance;

  const handleSubmit = () => {
    if (newTitle) {
      if (title !== newTitle) handleUpdateTitle(newTitle);
      setIsFocused(false);
      setError("");
    }
  };

  useOnClickOutside(inputRef, handleSubmit);

  return (
    <>
      <div ref={inputRef}>
        <div
          className={`edit-title-wrapper u-relative u-flex-column ${
            disabled ? "" : "hover:bg-accent"
          } ${isFocused ? "focus" : ""}`}
          ref={container}
          onMouseEnter={() => !disabled && !isFocused && setShowTooltip(true)}
          onMouseLeave={() => !disabled && setShowTooltip(false)}
        >
          <input
            disabled={disabled}
            onFocusCapture={() => {
              if (disabled) return;
              setIsFocused(true);
              setShowTooltip(false);
            }}
            className="w-full p-0 font-manrope text-lg font-bold bg-transparent border-0 focus:outline-none"
            value={newTitle}
            onChange={(e) => {
              const value = e.target.value;

              if (!value) setError(messages?.tickets?.errors?.titleRequired);
              else if (value.length > MAX_TEXT_INPUT_LENGTH) {
                setError(messages?.forms?.newTicketForm?.title?.lengthError);
                return;
              } else setError("");

              setNewTitle(e.target.value);
            }}
          />
          <div
            className={`tool-tip ${!showTooltip ? "tool-tip--hidden" : ""}`}
            ref={toolTip}
            style={styles.popper}
            {...attributes.popper}
          >
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-inverse">
              {messages?.tickets?.clickToEdit}
            </BodyText>
            <div className="arrow" data-popper-arrow />
          </div>
        </div>

        <div
          className={`u-items-center u-justify-between u-margin-t-1 ${
            isFocused ? "u-flex" : "u-hidden"
          }`}
        >
          <p className="input-v2-error-label u-padding-l-0 u-margin-r-1">
            {error && (
              <>
                <WarningCircle className="input-v2-error-icon" size={16} />
                {error}
              </>
            )}
          </p>
          <div className="u-flex">
            <ProcedureCheckCircle
              className={
                !newTitle ? "u-cursor-not-allowed" : "u-cursor-pointer"
              }
              onClick={(e) => {
                e.stopPropagation();
                handleSubmit();
              }}
            />
            <ProcedureCrossCircle
              className="u-cursor-pointer u-margin-l-1 u-margin-r-1"
              onClick={(e) => {
                e.stopPropagation();
                setIsFocused(false);
                setNewTitle(title);
                setError("");
              }}
            />
          </div>
        </div>
      </div>

      {isPMTicket && hasOriginalEventBeenDeleted && (
        <span style={PreventiveMaintenanceDeleteIconStyles}>
          &nbsp; (<ExclamationIcon /> &nbsp;&nbsp;
          {messages?.tickets?.preventiveEventDeletedMessage})
        </span>
      )}

      {isPMTicket &&
        !hasOriginalEventBeenDeleted &&
        maintenanceOn &&
        !maintenanceOn?.includes("1970-01-01") && (
          <>
            ({messages?.tickets?.preventiveTicketTitlePart}{" "}
            {moment(maintenanceOn).format(dateFormat) || ""})
          </>
        )}
    </>
  );
};
